import { Box, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "../StructuredPromptImageCanvas.module.scss";
import { BoundingBox } from "../types";
import { clamp } from "../utils/bboxDrag";

/** A drag smaller than this on either axis is treated as a mis-click, not an area. */
export const MIN_EMBED_SIZE = 3;

type Point = { x: number; y: number };

/**
 * Builds the % rect spanned by two canvas-relative pixel points. Both points are clamped
 * to the canvas first, so a drag that starts inside and ends off the image yields the
 * rect up to the edge instead of being lost; min/max means all four drag directions
 * produce the same rect.
 */
export const rectFromPoints = (a: Point, b: Point, canvasSize: { width: number; height: number }): BoundingBox => {
	const x1 = clamp(Math.min(a.x, b.x), 0, canvasSize.width);
	const x2 = clamp(Math.max(a.x, b.x), 0, canvasSize.width);
	const y1 = clamp(Math.min(a.y, b.y), 0, canvasSize.height);
	const y2 = clamp(Math.max(a.y, b.y), 0, canvasSize.height);

	return {
		left: (x1 / canvasSize.width) * 100,
		top: (y1 / canvasSize.height) * 100,
		width: ((x2 - x1) / canvasSize.width) * 100,
		height: ((y2 - y1) / canvasSize.height) * 100,
	};
};

interface EmbedAreaDrawLayerProps {
	canvasSize: { width: number; height: number };
	/** Called with the drawn rect (%) once a large-enough drag is released. */
	onCommit: (bbox: BoundingBox) => void;
	/** Esc, or the hint pill's Cancel — leaves draw mode with nothing drawn. */
	onCancel: () => void;
}

/**
 * Full-canvas crosshair surface for drawing the area to embed an image into. Sits above
 * everything else on the canvas; the canvas hides its bboxes/dots/inspector while this is
 * mounted, so the user is drawing over a clean image.
 */
export const EmbedAreaDrawLayer = ({ canvasSize, onCommit, onCancel }: EmbedAreaDrawLayerProps) => {
	const { t } = useTranslation("fibo", { keyPrefix: "lab.canvas" });
	// The container is captured on mousedown rather than looked up later: several results
	// can have a canvas mounted at once, so a document-wide query could pick the wrong one.
	const [drag, setDrag] = useState<{ origin: Point; container: Element } | null>(null);
	const [draft, setDraft] = useState<BoundingBox | null>(null);

	const handleMouseDown = (e: React.MouseEvent) => {
		e.stopPropagation();
		const layer = e.currentTarget as HTMLElement;
		const container = layer.closest("[data-canvas-container]") ?? layer;
		const rect = container.getBoundingClientRect();
		setDrag({ origin: { x: e.clientX - rect.left, y: e.clientY - rect.top }, container });
		setDraft(null);
	};

	// Tracked on window rather than the layer so a drag that leaves the image still
	// follows the pointer (same approach as useBboxManipulation).
	useEffect(() => {
		if (!drag) return;

		const { origin, container } = drag;

		const handleMouseMove = (e: MouseEvent) => {
			const rect = container.getBoundingClientRect();
			setDraft(rectFromPoints(origin, { x: e.clientX - rect.left, y: e.clientY - rect.top }, canvasSize));
		};

		const handleMouseUp = (e: MouseEvent) => {
			const rect = container.getBoundingClientRect();
			const finalRect = rectFromPoints(origin, { x: e.clientX - rect.left, y: e.clientY - rect.top }, canvasSize);
			setDrag(null);
			setDraft(null);
			// A too-small rect is discarded and draw mode stays armed, so a stray click
			// doesn't drop the user out of the flow.
			if (finalRect.width >= MIN_EMBED_SIZE && finalRect.height >= MIN_EMBED_SIZE) {
				onCommit(finalRect);
			}
		};

		window.addEventListener("mousemove", handleMouseMove);
		window.addEventListener("mouseup", handleMouseUp);
		return () => {
			window.removeEventListener("mousemove", handleMouseMove);
			window.removeEventListener("mouseup", handleMouseUp);
		};
	}, [drag, canvasSize, onCommit]);

	const handleKeyDown = useCallback(
		(e: KeyboardEvent) => {
			if (e.key === "Escape") onCancel();
		},
		[onCancel],
	);

	useEffect(() => {
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [handleKeyDown]);

	return (
		<Box className={styles.embedDrawLayer} data-embed-draw-layer onMouseDown={handleMouseDown}>
			{draft && (
				<Box
					className={styles.embedDraftRect}
					style={{
						left: `${(draft.left / 100) * canvasSize.width}px`,
						top: `${(draft.top / 100) * canvasSize.height}px`,
						width: `${(draft.width / 100) * canvasSize.width}px`,
						height: `${(draft.height / 100) * canvasSize.height}px`,
					}}
				>
					<Typography className={styles.embedSizeChip}>
						{t("embedSizeChip", { width: Math.round(draft.width), height: Math.round(draft.height) })}
					</Typography>
				</Box>
			)}
		</Box>
	);
};
